import React from 'react';
import './BillPreviewModal.css';
import { FiFileText, FiDownload, FiX } from 'react-icons/fi';
import { format } from 'date-fns';

const BillPreviewModal = ({ billData, customerInfo, onClose, onDownload, isDownloading }) => {
  if (!billData) return null;

  const formatCurrency = (amount) => {
    return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString || Date.now()), 'dd MMM yyyy, hh:mm a');
    } catch {
      return format(new Date(), 'dd MMM yyyy, hh:mm a');
    }
  };

  const summary = billData.billDetails.summary;
  const items = billData.billDetails.items;
  const explicitGstEnabled = billData.explicitGstEnabled === true;
  const taxes = explicitGstEnabled ? (billData.billDetails.taxes || []) : [];
  const discounts = billData.billDetails.discounts || [];
  const gstInclusiveMessage = billData.gstInclusiveMessage || 'Prices are inclusive of GST charges.';

  return (
    <div className="bill-modal-overlay" onClick={onClose}>
      <div className="bill-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="bill-modal-header">
          <h2>
            <FiFileText /> Bill Preview
          </h2>
          <button className="bill-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="bill-modal-content">
          <div className="bill-preview">
            {/* Header */}
            <div className="bill-header">
              <h1>RASTAURA</h1>
              <p className="subtitle">{explicitGstEnabled ? 'Tax Invoice' : 'Bill'}</p>
            </div>

            {/* Bill Info Section */}
            <div className="bill-info-section">
              <div className="bill-info-group">
                <h3>Bill Details</h3>
                <div className="bill-info-row">
                  <span className="bill-info-label">Customer Ticket:</span>
                  <span className="bill-info-value">{billData.customerTicketId || 'N/A'}</span>
                </div>
                <div className="bill-info-row">
                  <span className="bill-info-label">Date:</span>
                  <span className="bill-info-value">{formatDate()}</span>
                </div>
              </div>

              <div className="bill-info-group">
                <h3>Customer Information</h3>
                {customerInfo?.name && (
                  <div className="bill-info-row">
                    <span className="bill-info-label">Name:</span>
                    <span className="bill-info-value">{customerInfo.name}</span>
                  </div>
                )}
                {customerInfo?.phoneNumber && (
                  <div className="bill-info-row">
                    <span className="bill-info-label">Phone:</span>
                    <span className="bill-info-value">{customerInfo.phoneNumber}</span>
                  </div>
                )}
                {!customerInfo?.name && !customerInfo?.phoneNumber && (
                  <div className="bill-info-row">
                    <span className="bill-info-label">Guest</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items Section */}
            <div className="bill-items-section">
              <h3>Order Items</h3>
              <table className="bill-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unit_price)}</td>
                      <td>{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Discounts Section (if any) */}
            {discounts.length > 0 && (
              <div className="bill-discounts-section">
                <h4 style={{ color: '#10b981', marginBottom: '12px' }}>💰 Discounts Applied</h4>
                {discounts.map((discount, index) => (
                  <div key={index} className="bill-info-row" style={{ marginBottom: '8px' }}>
                    <span className="bill-info-label">
                      {discount.name} ({discount.percentage}%)
                    </span>
                    <span className="bill-info-value" style={{ color: '#10b981' }}>
                      -{formatCurrency(discount.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Summary Section */}
            <div className="bill-summary-section">
              <div className="bill-summary-row subtotal">
                <span className="bill-summary-label">Subtotal:</span>
                <span className="bill-summary-value">{formatCurrency(summary.net_amount)}</span>
              </div>

              {summary.total_discount > 0 && (
                <div className="bill-summary-row" style={{ color: '#10b981' }}>
                  <span className="bill-summary-label">Discount:</span>
                  <span className="bill-summary-value">-{formatCurrency(summary.total_discount)}</span>
                </div>
              )}

              {summary.service_charge_amount > 0 && (
                <div className="bill-summary-row tax">
                  <span className="bill-summary-label">Service Charge:</span>
                  <span className="bill-summary-value">{formatCurrency(summary.service_charge_amount)}</span>
                </div>
              )}

              {taxes.map((tax, index) => (
                <div key={index} className="bill-summary-row tax">
                  <span className="bill-summary-label">{tax.name} ({tax.percentage}%):</span>
                  <span className="bill-summary-value">{formatCurrency(tax.amount)}</span>
                </div>
              ))}

              {!explicitGstEnabled && (
                <div className="bill-tax-note">
                  {gstInclusiveMessage}
                </div>
              )}

              <div className="bill-summary-row total">
                <span className="bill-summary-label">GRAND TOTAL:</span>
                <span className="bill-summary-value">{formatCurrency(summary.grand_total)}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="bill-footer">
              <p className="thank-you">Thank you for dining with us!</p>
              <p>We hope to see you again soon.</p>
              <p className="timestamp">
                Generated: {format(new Date(), 'dd MMM yyyy, HH:mm:ss')}
              </p>
            </div>
          </div>
        </div>

        <div className="bill-modal-actions">
          <button className="bill-action-btn cancel" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="bill-action-btn download" 
            onClick={onDownload}
            disabled={isDownloading}
          >
            <FiDownload /> {isDownloading ? 'Downloading...' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillPreviewModal;

